import { useEffect, useState } from 'react';
import { IManagingAppPoolsInstance } from '../../api/models';
import { AppPoolAction } from '../../constants/AppPoolAction';
import { EventType } from '../../constants/EventType';
import {
    ServerApplicationClientEventSummaryHandler,
    ServerApplicationClientEventSummaryHandlerArgs,
} from '../ApplicationServerManagement/ServerApplicationClientEventSummaryHandler';
import useRefedProp from '../../hooks/useRefedProp';
import { Permission } from '../../permissions/permission';
import { UserPrincipal } from '../../permissions/userPrincipal';
import { ServerApplicationClientEventSummary } from '../../signalr/models';
import signalREventsClient from '../../signalr/SignalREventsClient';
import { ServerAppPoolDetails } from '../AppPoolsInfo/AppPoolsInfoView';
import { InfoModal } from '../Shared/InfoModal';
import { ActionButton } from '../UIExtentions/Buttons';
import { AppPoolServerRowProps } from './AppPoolsServerRow';

export interface AppPoolsServerRowActionsProps {
    serverApp: IManagingAppPoolsInstance;
    userPrincipal: UserPrincipal;
    serverUserPrincipal: UserPrincipal;
    onClickCallback: (action: AppPoolAction) => Promise<void>;
    onTimeoutCallback: (data: ServerApplicationClientEventSummary) => void;
}

export function AppPoolsServerRowActions(props: AppPoolServerRowProps) {
    const [showInfo, setShowInfo] = useState(false);
    const [startSpinning, setStartSpinning] = useState(false);
    const [stopSpinning, setStopSpinning] = useState(false);
    const [recycleSpinning, setRecycleSpinning] = useState(false);
    const [refreshSpinning, setRefreshSpinning] = useState(false);
    const onTimeoutCallbackRef = useRefedProp(props.onTimeoutCallback);

    const serverApp = props.serverApp;
    const userPrincipal = props.userPrincipal;
    const serverUserPrincipal = props.serverUserPrincipal;
    const onClickCallback = props.onClickCallback;

    const getEnableState = function (permission: Permission): boolean {
        return userPrincipal.hasPermission(permission) || serverUserPrincipal?.hasPermission(permission);
    };

    useEffect(() => {
        const appPoolStartHandler = new ServerApplicationClientEventSummaryHandler(serverApp.serverName, {
            addTypes: [EventType.AppPoolStartTriggered],
            failedTypes: [EventType.AppPoolStartFailed],
            successTypes: [EventType.AppPoolStartSucceeded],
            emptyCallback: () => setStartSpinning(false),
            addCallback: () => setStartSpinning(true),
            timeoutCallback: (data) => onTimeoutCallbackRef.current?.(data),
        } as ServerApplicationClientEventSummaryHandlerArgs);

        const appPoolStopHandler = new ServerApplicationClientEventSummaryHandler(serverApp.serverName, {
            addTypes: [EventType.AppPoolStopTriggered],
            failedTypes: [EventType.AppPoolStopFailed],
            successTypes: [EventType.AppPoolStopSucceeded],
            emptyCallback: () => setStopSpinning(false),
            addCallback: () => setStopSpinning(true),
            timeoutCallback: (data) => onTimeoutCallbackRef.current?.(data),
        } as ServerApplicationClientEventSummaryHandlerArgs);

        const appPoolRecycleHandler = new ServerApplicationClientEventSummaryHandler(serverApp.serverName, {
            addTypes: [EventType.AppPoolRecycleTriggered],
            failedTypes: [EventType.AppPoolRecycleFailed],
            successTypes: [EventType.AppPoolRecycleSucceeded],
            emptyCallback: () => setRecycleSpinning(false),
            addCallback: () => setRecycleSpinning(true),
            timeoutCallback: (data) => onTimeoutCallbackRef.current?.(data),
        } as ServerApplicationClientEventSummaryHandlerArgs);

        const appPoolRefreshHandler = new ServerApplicationClientEventSummaryHandler(serverApp.serverName, {
            addTypes: [EventType.AppPoolRefreshTriggered],
            failedTypes: [EventType.AppPoolRefreshFailed],
            successTypes: [EventType.AppPoolRefreshSucceeded],
            emptyCallback: () => setRefreshSpinning(false),
            addCallback: () => setRefreshSpinning(true),
            timeoutCallback: (data) => onTimeoutCallbackRef.current?.(data),
        } as ServerApplicationClientEventSummaryHandlerArgs);

        function isForMe(data: ServerApplicationClientEventSummary) {
            return data.targetApplication === serverApp.name && data.targetServer === serverApp.serverName;
        }

        signalREventsClient.onAppPoolEvent.setHook(`AppPoolsServerRow_${serverApp.name}_${serverApp.serverName}`, (data) => {
            if (isForMe(data)) {
                appPoolStartHandler.handleItem(data);
                appPoolStopHandler.handleItem(data);
                appPoolRecycleHandler.handleItem(data);
                appPoolRefreshHandler.handleItem(data);
            }
        });
    }, [onTimeoutCallbackRef, serverApp.name, serverApp.serverName]);

    const infoModalTitle = `${serverApp.name} - ${serverApp.serverName}`;

    return (
        <>
            <td>
                <ActionButton
                    disabled={!getEnableState(Permission.AppPool_Refresh)}
                    spinning={refreshSpinning}
                    icon="fa-refresh"
                    variant="btn-dark-blue"
                    title="Refresh"
                    onClick={() => onClickCallback(AppPoolAction.Refresh)}
                />
            </td>
            <td></td>
            <td className="text-center">
                <ActionButton
                    disabled={!getEnableState(Permission.AppPool_Recycle)}
                    spinning={recycleSpinning}
                    icon="fa-recycle"
                    variant="btn-blue"
                    title="Recycle"
                    onClick={() => onClickCallback(AppPoolAction.Recycle)}
                />
                |
                <ActionButton
                    disabled={!getEnableState(Permission.AppPool_Start)}
                    spinning={startSpinning}
                    icon="fa-play"
                    variant="btn-green"
                    title="Start"
                    onClick={async () => await onClickCallback(AppPoolAction.Start)}
                />
                <ActionButton
                    disabled={!getEnableState(Permission.AppPool_Stop)}
                    spinning={stopSpinning}
                    icon="fa-stop"
                    variant="btn-red"
                    title="Stop"
                    onClick={async () => await onClickCallback(AppPoolAction.Stop)}
                />
                |
                <ActionButton icon="fa-info-circle" variant="btn-dark-blue" title="Info" onClick={() => setShowInfo(true)} />
                <InfoModal title={infoModalTitle} show={showInfo} onHide={() => setShowInfo(false)}>
                    <ServerAppPoolDetails serverApp={serverApp}></ServerAppPoolDetails>
                </InfoModal>
            </td>
        </>
    );
}
